/**
 * Tenant Create — Multi-step wizard with validation.
 * @see Documentation/Specification/020_Definitions/application-agnostic/identity/Tenant.md
 */
import { useState } from 'react';
import { useCreate, useNavigate, useNotify } from 'react-admin';
import {
  Box, Typography, Paper, Button, Grid, Stepper, Step, StepLabel, CircularProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { TENANT_STEPS } from './types';

interface CreateForm {
  name: string;
  registeredName: string;
  registrationNumber: string;
  email: string;
  phone: string;
  address: string;
  currency: string;
  timezone: string;
  dateFormat: string;
  country: string;
}

const INITIAL_FORM: CreateForm = {
  name: '', registeredName: '', registrationNumber: '',
  email: '', phone: '', address: '',
  currency: 'ZAR', timezone: 'Africa/Johannesburg', dateFormat: 'dd/MM/yyyy', country: 'ZA',
};

const inputSx = { width: '100%', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, fontSize: 14 };

export const TenantCreate = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [create] = useCreate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState<CreateForm>(INITIAL_FORM);

  const update = (field: keyof CreateForm, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (step === 0) {
      if (!form.name || form.name.length < 2) e.name = 'Name must be at least 2 characters';
    }
    if (step === 1) {
      if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
      if (!form.phone) e.phone = 'Phone required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => { if (validate()) setStep((s) => Math.min(s + 1, 3)); };
  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await create('tenants', {
        data: {
          name: form.name,
          registeredName: form.registeredName || undefined,
          registrationNumber: form.registrationNumber || undefined,
          contact: { email: form.email, phone: form.phone, address: form.address || undefined },
          settings: { currency: form.currency, timezone: form.timezone, dateFormat: form.dateFormat, country: form.country },
        },
      });
      notify('Tenant created', { type: 'success' });
      navigate('/tenants');
    } catch (err: any) {
      notify(err?.message ?? 'Failed to create tenant', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 700, mx: 'auto' }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/tenants')} sx={{ mb: 2 }}>
        Back to Tenants
      </Button>
      <Typography variant="h5" fontWeight={600} mb={3}>Create Tenant</Typography>

      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {TENANT_STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>

      <Paper sx={{ p: 3 }}>
        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Name *</Typography>
              <Box component="input" value={form.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => update('name', e.target.value)}
                sx={{ ...inputSx, borderColor: errors.name ? 'error.main' : 'divider' }} />
              {errors.name && <Typography variant="caption" color="error">{errors.name}</Typography>}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Registered Name</Typography>
              <Box component="input" value={form.registeredName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => update('registeredName', e.target.value)}
                sx={inputSx} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Registration Number</Typography>
              <Box component="input" value={form.registrationNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => update('registrationNumber', e.target.value)}
                sx={inputSx} />
            </Box>
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Email *</Typography>
              <Box component="input" type="email" value={form.email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => update('email', e.target.value)}
                sx={{ ...inputSx, borderColor: errors.email ? 'error.main' : 'divider' }} />
              {errors.email && <Typography variant="caption" color="error">{errors.email}</Typography>}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Phone *</Typography>
              <Box component="input" value={form.phone} onChange={(e: React.ChangeEvent<HTMLInputElement>) => update('phone', e.target.value)}
                sx={{ ...inputSx, borderColor: errors.phone ? 'error.main' : 'divider' }} />
              {errors.phone && <Typography variant="caption" color="error">{errors.phone}</Typography>}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Address</Typography>
              <Box component="input" value={form.address} onChange={(e: React.ChangeEvent<HTMLInputElement>) => update('address', e.target.value)}
                sx={inputSx} />
            </Box>
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {([
              ['Currency', 'currency'] as const,
              ['Timezone', 'timezone'] as const,
              ['Date Format', 'dateFormat'] as const,
              ['Country', 'country'] as const,
            ]).map(([label, field]) => (
              <Box key={field}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Box component="input" value={form[field]} onChange={(e: React.ChangeEvent<HTMLInputElement>) => update(field, e.target.value)}
                  sx={inputSx} />
              </Box>
            ))}
          </Box>
        )}

        {step === 3 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>Review</Typography>
            <Grid container spacing={2}>
              {([
                ['Name', form.name],
                ['Registered Name', form.registeredName || '—'],
                ['Registration #', form.registrationNumber || '—'],
                ['Email', form.email],
                ['Phone', form.phone],
                ['Address', form.address || '—'],
                ['Currency', form.currency],
                ['Timezone', form.timezone],
                ['Date Format', form.dateFormat],
                ['Country', form.country],
              ] as const).map(([label, value]) => (
                <Grid size={{ xs: 6 }} key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2">{value}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button disabled={step === 0} onClick={handleBack}>Back</Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => navigate('/tenants')}>Cancel</Button>
          {step < 3 ? (
            <Button variant="contained" onClick={handleNext}>Next</Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? <CircularProgress size={20} /> : 'Create Tenant'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};
